import { motion } from "framer-motion";
import * as React from "react";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";

export interface NavItem {
  to: string;
  label: string;
  /** Lucide icon component or any element. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Match the path exactly (use for "/"). */
  end?: boolean;
  disabled?: boolean;
  /** Show a compact icon-only label until this breakpoint. */
  labelVisibleFrom?: "sm" | "md" | "lg";
  /** Hide this item below a breakpoint. */
  visibleFrom?: "sm" | "md" | "lg";
}

export interface NavProps {
  items: readonly NavItem[];
  /** Default "horizontal". Vertical suits sidebars. */
  orientation?: "horizontal" | "vertical";
  /** Hide labels below the sm breakpoint when an icon exists. Default true for horizontal. */
  compactOnMobile?: boolean;
  /** Landmark name. Default "Main". */
  label?: string;
  className?: string;
}

const labelBreakpoints = {
  sm: "sm:not-sr-only",
  md: "md:not-sr-only",
  lg: "lg:not-sr-only",
} as const;

const visibilityBreakpoints = {
  sm: "hidden sm:list-item",
  md: "hidden md:list-item",
  lg: "hidden lg:list-item",
} as const;

/** Router-aware navigation with an animated gold pill on the active item. */
export function Nav({
  items,
  orientation = "horizontal",
  compactOnMobile = orientation === "horizontal",
  label = "Main",
  className,
}: NavProps) {
  const pillId = React.useId();
  const vertical = orientation === "vertical";
  return (
    <nav aria-label={label} className={className}>
      <ul className={cn("flex gap-1", vertical ? "flex-col" : "flex-row items-center")}>
        {items.map(
          ({
            to,
            label: itemLabel,
            icon: Icon,
            end,
            disabled,
            labelVisibleFrom = "sm",
            visibleFrom,
          }) => (
            <li key={to} className={visibleFrom ? visibilityBreakpoints[visibleFrom] : undefined}>
              <NavLink
                to={to}
                end={end}
                aria-disabled={disabled || undefined}
                tabIndex={disabled ? -1 : undefined}
                onClick={(event) => disabled && event.preventDefault()}
                className={({ isActive }) =>
                  cn(
                    "relative isolate flex h-10 items-center gap-2 rounded-[var(--radius-lg)] px-3 font-heading text-[length:var(--font-size-sm)] font-semibold tracking-wide",
                    "transition-colors duration-150 focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]",
                    isActive
                      ? "text-ds-gold-light"
                      : "text-ds-text-muted hover:text-ds-gold-accent",
                    disabled && "pointer-events-none opacity-40",
                    vertical && "w-full",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        layoutId={`ds-nav-pill-${pillId}`}
                        aria-hidden="true"
                        className="absolute inset-0 -z-10 rounded-[var(--radius-lg)] border border-ds-gold-primary/50 bg-ds-gold-primary/15"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                    {Icon && <Icon className="relative h-4 w-4 shrink-0" aria-hidden="true" />}
                    <span
                      className={cn(
                        "relative",
                        Icon && compactOnMobile && "sr-only",
                        Icon && compactOnMobile && labelBreakpoints[labelVisibleFrom],
                      )}
                    >
                      {itemLabel}
                    </span>
                  </>
                )}
              </NavLink>
            </li>
          ),
        )}
      </ul>
    </nav>
  );
}
